/**
 * Bird-game difficulty segments (continuous adventure: easy → medium → hard).
 * Tunables live here so homeSketch stays free of scattered conditionals.
 *
 * Personalities:
 *   Easy   — Treasure Hunt (sunshine weather, decorative only)
 *   Medium — Risk vs Reward (breezy; wind + bonus ground flowers)
 *   Hard   — Survival Mode (storm + wind; lighthouse beam restores one zap)
 */

export const DIFFICULTY_STORAGE_KEY = 'wesam-bird-difficulty';
export const HIGH_SCORE_STORAGE_KEY = 'wesam-bird-high-score';
export const DEFAULT_DIFFICULTY = 'easy';

/** @type {Record<string, {
 *   id: string,
 *   label: string,
 *   personality: string,
 *   weather: 'sunshine' | 'breezy' | 'storm',
 *   moveDivisor: number,
 *   hitPad: number,
 *   creatureHitPad: number,
 *   scoreMultiplier: number,
 *   comboWindowMs: number,
 *   comboBonus: number,
 *   collectBurstCount: number,
 *   progressiveIntervalMs: number,
 *   progressiveScoreBump: number,
 *   wormSpawnIntervalMs: number,
 *   wormVisibleMs: number,
 *   wormEmergeMs: number,
 *   wormMax: number,
 *   wormPoints: number,
 *   snakeSpawnIntervalMs: number,
 *   snakeVisibleMs: number,
 *   snakeEmergeMs: number,
 *   snakeMax: number,
 *   snakePenalty: number,
 *   maxZaps: number,
 *   windGustChance?: number,
 *   windGustCooldownMs?: number,
 *   windFirstDelayMs?: number,
 *   windIntervalMs?: number,
 *   windIntervalJitterMs?: number,
 *   flowerSpawnIntervalMs?: number,
 *   flowerVisibleMs?: number,
 *   flowerEmergeMs?: number,
 *   flowerMax?: number,
 *   flowerPoints?: number,
 *   hasLighthouse?: boolean,
 *   beamCooldownMs?: number,
 *   beamHealMs?: number,
 *   lightningIntervalMs?: number,
 *   lightningJitterMs?: number,
 *   lightningFirstDelayMs?: number,
 *   lightningFlashMs?: number,
 *   lightningHitPad?: number,
 * }>} */
export const DIFFICULTIES = {
  easy: {
    id: 'easy',
    label: 'Easy',
    personality: 'Treasure Hunt',
    weather: 'sunshine',
    // Larger steps → fewer keypresses to reach coins / next page
    moveDivisor: 12,
    hitPad: 120,
    creatureHitPad: 95,
    scoreMultiplier: 1,
    comboWindowMs: 2800,
    comboBonus: 5,
    collectBurstCount: 14,
    progressiveIntervalMs: 45000,
    progressiveScoreBump: 2,
    // Frequent worms, almost no snakes, relaxed pace
    wormSpawnIntervalMs: 2800,
    wormVisibleMs: 3200,
    wormEmergeMs: 700,
    wormMax: 2,
    wormPoints: 100,
    snakeSpawnIntervalMs: 22000,
    snakeVisibleMs: 2400,
    snakeEmergeMs: 900,
    snakeMax: 1,
    snakePenalty: 100,
    maxZaps: 0,
  },
  medium: {
    id: 'medium',
    label: 'Medium',
    personality: 'Risk vs Reward',
    weather: 'breezy',
    moveDivisor: 15,
    hitPad: 100,
    creatureHitPad: 88,
    scoreMultiplier: 1.5,
    comboWindowMs: 1800,
    comboBonus: 8,
    collectBurstCount: 18,
    progressiveIntervalMs: 30000,
    progressiveScoreBump: 4,
    wormSpawnIntervalMs: 5500,
    wormVisibleMs: 2400,
    wormEmergeMs: 520,
    wormMax: 1,
    wormPoints: 100,
    snakeSpawnIntervalMs: 4500,
    snakeVisibleMs: 2200,
    snakeEmergeMs: 480,
    snakeMax: 2,
    snakePenalty: 100,
    maxZaps: 0,
    // Frequent gusts: timed ambient + high chance on move
    windGustChance: 0.75,
    windGustCooldownMs: 550,
    windFirstDelayMs: 600,
    windIntervalMs: 1200,
    windIntervalJitterMs: 500,
    // Bonus flowers growing from the ground
    flowerSpawnIntervalMs: 3800,
    flowerVisibleMs: 2800,
    flowerEmergeMs: 600,
    flowerMax: 2,
    flowerPoints: 50,
  },
  hard: {
    id: 'hard',
    label: 'Hard',
    personality: 'Survival Mode',
    weather: 'storm',
    // Smaller steps → more precise control
    moveDivisor: 20,
    hitPad: 78,
    creatureHitPad: 80,
    scoreMultiplier: 3,
    comboWindowMs: 1100,
    comboBonus: 12,
    collectBurstCount: 22,
    progressiveIntervalMs: 20000,
    progressiveScoreBump: 6,
    // Rare high-value worms, 8–10× snake pressure vs Easy
    wormSpawnIntervalMs: 12000,
    wormVisibleMs: 1800,
    wormEmergeMs: 380,
    wormMax: 1,
    wormPoints: 250,
    snakeSpawnIntervalMs: 2200,
    snakeVisibleMs: 2000,
    snakeEmergeMs: 280,
    snakeMax: 4,
    snakePenalty: 100,
    maxZaps: 3,
    // Storm still blows on a timer + moves
    windGustChance: 0.55,
    windGustCooldownMs: 800,
    windFirstDelayMs: 900,
    windIntervalMs: 1600,
    windIntervalJitterMs: 700,
    // Lighthouse beam: each sweep over the bird recovers one zap
    hasLighthouse: true,
    beamCooldownMs: 8500,
    beamHealMs: 900,
    lightningIntervalMs: 4800,
    lightningJitterMs: 2400,
    lightningFirstDelayMs: 3200,
    lightningFlashMs: 300,
    lightningHitPad: 58,
  },
};

export const DIFFICULTY_ORDER = ['easy', 'medium', 'hard'];

export function getDifficulty(id) {
  return DIFFICULTIES[id] || DIFFICULTIES[DEFAULT_DIFFICULTY];
}

export function readStoredDifficulty() {
  try {
    const raw = localStorage.getItem(DIFFICULTY_STORAGE_KEY);
    if (raw && DIFFICULTIES[raw]) return raw;
  } catch {
    // ignore
  }
  return DEFAULT_DIFFICULTY;
}

export function storeDifficulty(id) {
  if (!DIFFICULTIES[id]) return;
  try {
    localStorage.setItem(DIFFICULTY_STORAGE_KEY, id);
  } catch {
    // ignore
  }
}

export function readHighScore() {
  try {
    const n = parseInt(localStorage.getItem(HIGH_SCORE_STORAGE_KEY) || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function writeHighScore(score) {
  const next = Math.floor(score);
  if (!Number.isFinite(next) || next <= 0) return;
  try {
    const prev = readHighScore();
    if (next > prev) {
      localStorage.setItem(HIGH_SCORE_STORAGE_KEY, String(next));
    }
  } catch {
    // ignore
  }
}
